import * as React from "react";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { TooltipWithContent } from "@/components/ui/tooltip";
import type { SelectOption } from "./types";

interface ResponsiveCommandSelectTriggerProps {
  selected: SelectOption | null;
  placeholder: string;
  disabled: boolean;
  className?: string;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  onClick?: (e: React.MouseEvent) => void;
  /** Portal tooltip into this element (e.g. modal dialog) so it renders inside and displays above modal content */
  tooltipContainer?: HTMLElement | null;
  "aria-expanded"?: boolean;
  "aria-haspopup"?: "listbox" | "dialog";
}

export function ResponsiveCommandSelectTrigger({
  selected,
  placeholder,
  disabled,
  className,
  triggerRef,
  onClick,
  tooltipContainer,
  "aria-expanded": ariaExpanded,
  "aria-haspopup": ariaHaspopup,
}: ResponsiveCommandSelectTriggerProps) {
  const displayText = selected
    ? selected.description
      ? `${selected.label} (${selected.description})`
      : selected.label
    : null;

  const triggerContent = (
    <Button
      type="button"
      variant="outline"
      className={cn("w-full", className)}
      disabled={disabled}
      ref={triggerRef}
      onClick={onClick}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHaspopup}
    >
      {selected ? (
        <div className="flex-1 min-w-0 text-left truncate">
          <span className="truncate">{selected.label}</span>
          {selected.description && (
            <span className="ml-1 text-xs text-muted-foreground">({selected.description})</span>
          )}
        </div>
      ) : (
        <span className="truncate">{placeholder}</span>
      )}
      <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
    </Button>
  );

  if (!(selected && displayText)) {
    return triggerContent;
  }

  return (
    <TooltipWithContent
      content={displayText}
      position="top"
      sideOffset={4}
      tooltipContainer={tooltipContainer ?? undefined}
    >
      {triggerContent}
    </TooltipWithContent>
  );
}
